import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { API } from "@/lib/api";
import { Eyebrow, Reveal } from "@/components/Reveal";

const FIELD =
  "w-full border-b border-cream/25 bg-transparent py-3 text-cream outline-none transition-colors placeholder:text-cream/35 focus:border-gold";

export default function Footer() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      setSent(true);
      toast.success("Message received — we reply within one business day.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer
      id="contact"
      className="grain relative bg-ink text-cream"
      data-testid="site-footer"
    >
      <div className="relative z-10 mx-auto max-w-[1440px] px-6 py-20 md:px-10 md:py-28">
        <div className="grid gap-16 md:grid-cols-2">
          <div>
            <Eyebrow light>Contact</Eyebrow>
            <Reveal>
              <h2 className="mt-5 font-display text-4xl leading-[1.05] md:text-6xl">
                Begin the <em className="text-gold">conversation</em>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-md leading-relaxed text-cream/70">
                Villas, land or a managed investment — tell us what you are
                looking for and an advisor on the island will reply within one
                business day.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 flex flex-wrap gap-x-10 gap-y-4 font-mono text-[11px] uppercase tracking-[0.25em] text-cream/50">
                <a
                  href="mailto:info@temerariolombok.com"
                  className="text-gold transition-colors hover:text-cream"
                  data-testid="footer-email"
                >
                  info@temerariolombok.com
                </a>
                <span>Kuta Selatan · Lombok</span>
                <span>Licensing & escrow guided</span>
                <span>English · Bahasa Indonesia</span>
              </div>
            </Reveal>
          </div>

          <div data-testid="footer-contact-form">
            {sent ? (
              <div
                className="flex h-full min-h-[280px] flex-col items-start justify-center gap-4"
                data-testid="footer-contact-success"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-gold text-gold">
                  <Check className="h-5 w-5" />
                </span>
                <p className="font-display text-2xl">Terima kasih — received.</p>
                <p className="text-cream/60">
                  An advisor will be in touch within one business day.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  <input
                    required
                    placeholder="Full name"
                    className={FIELD}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="footer-contact-name-input"
                  />
                  <input
                    required
                    type="email"
                    placeholder="Email address"
                    className={FIELD}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="footer-contact-email-input"
                  />
                </div>
                <textarea
                  required
                  rows={4}
                  placeholder="What are you looking for?"
                  className={`${FIELD} resize-none`}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="footer-contact-message-input"
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="footer-contact-submit"
                  className="group inline-flex items-center gap-3 rounded-full bg-cream px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-colors hover:bg-gold disabled:opacity-50"
                >
                  Send message
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="mt-20 flex flex-col gap-6 border-t border-cream/10 pt-8 md:flex-row md:items-center md:justify-between">
          <span className="font-display text-lg">
            PACHAMAMA <em className="text-gold">Lombok</em>
          </span>
          <nav className="flex flex-wrap gap-x-8 gap-y-2">
            {[
              { to: "/lombok", label: "Lombok" },
              { to: "/portfolio", label: "Portfolio" },
              { to: "/investor-club", label: "Investor Club" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`footer-link-${l.label.toLowerCase().replace(" ", "-")}`}
                className="font-mono text-[11px] uppercase tracking-[0.2em] text-cream/60 transition-colors hover:text-gold"
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
            © {new Date().getFullYear()} Pachamama Lombok — www.temerariolombok.com
          </p>
        </div>
      </div>
    </footer>
  );
}
